/**
 * The two lists a Custom MCP connection carries: which of its tools the
 * assistant may use, and which groups' members may use them. Each saves on its
 * own; both refresh the connections list, which is where the page reads from.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { TenantIntegrationSummary } from '../../lib/types.js';

export interface McpOAuthClientInput {
  clientId: string;
  /** Omitted keeps the secret already held. */
  clientSecret?: string;
}

export interface McpConnectionLogic {
  saveTools: (integrationId: string, tools: string[]) => Promise<TenantIntegrationSummary>;
  saveGroups: (integrationId: string, groupIds: string[]) => Promise<TenantIntegrationSummary>;
  /** Sign-in connections: the client the admin registered with the login system. */
  saveClient: (
    integrationId: string,
    client: McpOAuthClientInput,
  ) => Promise<TenantIntegrationSummary>;
  isSaving: boolean;
  error: Error | null;
}

export const useMcpConnectionLogic = (tenantId: string | undefined): McpConnectionLogic => {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations(tenantId ?? '') });
  };

  const tools = useMutation<
    TenantIntegrationSummary,
    Error,
    { integrationId: string; tools: string[] }
  >({
    mutationFn: ({ integrationId, tools: names }) =>
      apiRequest<TenantIntegrationSummary>(`/api/integrations/${integrationId}/mcp-tools`, {
        method: 'PUT',
        body: { tools: names },
      }),
    onSuccess: refresh,
  });

  const groups = useMutation<
    TenantIntegrationSummary,
    Error,
    { integrationId: string; groupIds: string[] }
  >({
    mutationFn: ({ integrationId, groupIds }) =>
      apiRequest<TenantIntegrationSummary>(`/api/integrations/${integrationId}/mcp-groups`, {
        method: 'PUT',
        body: { groupIds },
      }),
    onSuccess: refresh,
  });

  const client = useMutation<
    TenantIntegrationSummary,
    Error,
    { integrationId: string; client: McpOAuthClientInput }
  >({
    mutationFn: ({ integrationId, client: body }) =>
      apiRequest<TenantIntegrationSummary>(`/api/integrations/${integrationId}/mcp-oauth-client`, {
        method: 'PUT',
        body,
      }),
    onSuccess: refresh,
  });

  return {
    saveTools: (integrationId, names) => tools.mutateAsync({ integrationId, tools: names }),
    saveGroups: (integrationId, groupIds) => groups.mutateAsync({ integrationId, groupIds }),
    saveClient: (integrationId, body) => client.mutateAsync({ integrationId, client: body }),
    isSaving: tools.isPending || groups.isPending || client.isPending,
    error: tools.error ?? groups.error ?? client.error,
  };
};
